import React, { SetStateAction } from 'react';

import { Box, Chip, Skeleton } from '@mui/material';
import { CardBody, CardTitle } from '@apigrowfinance/pbolt-card';
import { ChipButton } from '@apigrowfinance/pbolt-chip-button';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage } from 'react-intl';

import { CustomersItem } from '@clients/customersClient';
import getEnumTranslation from '@utils/getEnumTranslation';

type CustomerChipsProps = {
    customers: CustomersItem[];
    selectedCustomer: number;
    isLoadingCustomers: boolean;
    onSelect: (customerId: number | null) => void;
    setControlTypeModalOpen: React.Dispatch<SetStateAction<boolean>>;
    displayAddButton: boolean;
};

const FlexControlChips = ({
    customers,
    selectedCustomer,
    onSelect,
    isLoadingCustomers,
    setControlTypeModalOpen,
    displayAddButton,
}: CustomerChipsProps) => {
    const { theme } = useTheme();

    return (
        <>
            <CardBody isGap={false}>
                {displayAddButton && (
                    <Box
                        data-testid="add-control-button"
                        sx={{
                            display: 'flex',
                            justifyContent: 'end',
                        }}>
                        <ChipButton
                            isLoading={isLoadingCustomers}
                            onClick={() => setControlTypeModalOpen(true)}
                            label={
                                <FormattedMessage
                                    id="Controls.addControl"
                                    defaultMessage="Add control"
                                />
                            }
                            variant="addMenu"
                            bordered
                        />
                    </Box>
                )}
                <Box sx={{ flexGrow: 1 }}>
                    <CardTitle>
                        <FormattedMessage
                            id="Controls.controls"
                            defaultMessage="Controls"
                        />
                    </CardTitle>
                </Box>
            </CardBody>
            <CardBody direction="row">
                <Chip
                    onClick={() => onSelect(null)}
                    label={getEnumTranslation('account')}
                    sx={{
                        mr: 1,
                        ...(!selectedCustomer
                            ? {
                                  color: 'primary.contrastText',
                                  bgcolor: 'primary.main',
                                  '&:hover': {
                                      bgcolor: 'primary.dark',
                                  },
                              }
                            : {
                                  bgcolor: 'background.default',
                                  color: theme.palette.text.primary,
                                  '&:hover': {
                                      color: 'primary.dark',
                                      bgcolor: 'grey.100',
                                  },
                              }),
                    }}
                />
                {isLoadingCustomers && (
                    <Chip
                        label={
                            <Skeleton
                                data-testid="customer-loading-chip"
                                width={80}
                            />
                        }
                        sx={{
                            mr: 1,
                            bgcolor: 'background.default',
                            color: theme.palette.text.primary,
                            '&:hover': {
                                color: 'primary.dark',
                                bgcolor: 'grey.100',
                            },
                        }}
                    />
                )}
                {!isLoadingCustomers && (
                    <>
                        {customers?.map((customer) => {
                            return (
                                <Chip
                                    data-testid={`${customer?.customer?.id}-customer-chip`}
                                    key={customer?.customer?.id}
                                    onClick={() =>
                                        onSelect(customer?.customer?.id)
                                    }
                                    label={customer?.customer?.printed_name}
                                    sx={{
                                        mr: 1,
                                        ...(customer?.customer?.id ===
                                        selectedCustomer
                                            ? {
                                                  color: 'primary.contrastText',
                                                  bgcolor: 'primary.main',
                                                  '&:hover': {
                                                      bgcolor: 'primary.dark',
                                                  },
                                              }
                                            : {
                                                  bgcolor: 'background.default',
                                                  color: theme.palette.text
                                                      .primary,
                                                  '&:hover': {
                                                      color: 'primary.dark',
                                                      bgcolor: 'grey.100',
                                                  },
                                              }),
                                    }}
                                />
                            );
                        })}
                    </>
                )}
            </CardBody>
        </>
    );
};

export default FlexControlChips;
